import { EAGLE_BOARD_MAP_IDS, mapsFromCatalog } from '../maps'
import { storyAt } from '../storyTime'
import type { ScenarioSession } from '../types'

/** Sentinel story origin, 10:00 in the session timezone (America/Chicago, CDT). */
const ORIGIN = '2026-09-22T10:00:00-05:00'

/**
 * Framed on the client's Eagle Mountain town style (subdivision, Kaiser Rd,
 * Yucca Dr, school, pipelines, irrigation laterals).
 */
const CAM_LNG = -115.478
const CAM_LAT = 33.852

const WEST_CMD = { lng: -115.4894, lat: 33.8582 }
const WEST_CMD_LABEL = { lng: -115.4914, lat: 33.8565 }
/** Work-zone hold — nearest point on Kaiser Rd to the old pinch. */
const PINCH = { lng: -115.474716, lat: 33.854189 }
const SCHOOL_FIRE = { lng: -115.4712, lat: 33.8509 }
const SCHOOL_BAY = { lng: -115.4728, lat: 33.8518 }
const TANK_FARM = { lng: -115.4918, lat: 33.8574 }
const NOTE_POS = { lng: -115.4758, lat: 33.8606 }

/**
 * One Mapbox/OSM segment of Kaiser Road (Eagle Mountain). Stored as an
 * attached asset so Inspect shows the yellow link highlight; truck keyframes
 * sample along the same path.
 */
const KAISER_RD: [number, number][] = [
  [-115.47288, 33.853382],
  [-115.477082, 33.855229],
  [-115.478273, 33.855763],
  [-115.47912, 33.85603],
  [-115.480291, 33.856621],
  [-115.480371, 33.856661],
  [-115.48148, 33.856968],
  [-115.482086, 33.856973],
  [-115.48265, 33.856955],
  [-115.486167, 33.857706],
  [-115.486537, 33.857764],
]
const KAISER_MID = { lng: -115.479463, lat: 33.856203 }

export const EAGLE_MOUNTAIN_INCIDENT_ID = 'eagle-mountain-yard'

export function createEagleMountainIncidentSession(): ScenarioSession {
  return {
    id: EAGLE_MOUNTAIN_INCIDENT_ID,
    title: 'Eagle Mountain cascade',
    intro: 'Multiple failures stack near Eagle Mountain. Watch Kaiser Road, then the west collector and the school.',
    summary:
      'Near Eagle Mountain, a Kaiser Road truck op is interrupted by a tank leak and west-collector rupture, then the road closes. A school-side fire is fanned by a wind gust before EMS rolls from West command.',
    storyStartTime: ORIGIN,
    storyEndTime: storyAt(ORIGIN, 120_000),
    timezone: 'America/Chicago',
    showTimeZone: false,
    displayUnits: 'imperial',
    boardComplexity: 'everything',
    maps: mapsFromCatalog(EAGLE_BOARD_MAP_IDS),
    activeMapId: 'eagle-mountain',
    camera: { lng: CAM_LNG, lat: CAM_LAT, zoom: 14.55 },
    comments: [
      {
        id: 'sc-note-hello',
        createdAt: storyAt(ORIGIN, 0),
        createdAtWallIso: '2026-09-11T05:00:00.000Z',
        authorObjectId: 'icp-1',
        source: 'script',
        body: 'Hello everyone, let’s get started. Watch the truck on Kaiser Road, then the west collector.',
      },
      {
        id: 'sc-note-close',
        createdAt: storyAt(ORIGIN, 120_000),
        createdAtWallIso: '2026-09-11T05:02:00.000Z',
        authorObjectId: 'icp-1',
        source: 'script',
        body: 'Great work, everyone. Here’s what we learned.',
      },
    ],
    objects: [
      {
        id: 'wind-1',
        type: 'wind',
        label: 'Surface wind',
        scope: 'global',
        pose: null,
        size: 'md',
        showOnTimeline: true,
        params: {
          fromDeg: 270,
          speedMph: 12,
          plumeDistanceM: 520,
          plumeSpreadDeg: 28,
        },
        comments: [],
      },
      {
        id: 'label-1',
        type: 'label',
        label: 'West command',
        scope: 'local',
        pose: WEST_CMD_LABEL,
        size: 'lg',
        params: { text: 'West command' },
        comments: [],
      },
      {
        id: 'icp-1',
        type: 'icp',
        label: 'ICP',
        scope: 'local',
        pose: { lng: -115.4868, lat: 33.8566 },
        size: 'md',
        params: {},
        comments: [],
      },
      {
        id: 'staging-1',
        type: 'staging',
        label: 'Yard staging',
        scope: 'local',
        pose: { lng: -115.4852, lat: 33.8591 },
        size: 'md',
        params: {},
        comments: [],
      },
      {
        id: 'road-1',
        type: 'road',
        label: 'Kaiser Road',
        scope: 'local',
        pose: KAISER_MID,
        size: 'md',
        description:
          'Linked to the Kaiser Road map asset. Turns red when closed; the lowboy holds at the work zone.',
        params: {
          status: 'open',
          segments: [KAISER_RD],
          attachedAssets: [
            {
              kind: 'road',
              name: 'Kaiser Road',
              properties: { name: 'Kaiser Road', class: 'tertiary' },
              path: KAISER_RD,
              segments: [KAISER_RD],
            },
          ],
        },
        path: KAISER_RD,
        comments: [],
      },
      {
        id: 'truck-1',
        type: 'truck',
        label: 'Lowboy / crane',
        scope: 'local',
        pose: PINCH,
        size: 'lg',
        description:
          'Patrols along the linked Kaiser Road segment. Road-close holds it at the work zone.',
        params: {
          holdLng: PINCH.lng,
          holdLat: PINCH.lat,
        },
        relatedObjectIds: ['road-1'],
        loopKeyframes: true,
        keyframes: [
          // Out-and-back along Kaiser Rd (~1.4 km), same cadence as before.
          { at: storyAt(ORIGIN, 0), lng: -115.47288, lat: 33.853382 },
          { at: storyAt(ORIGIN, 4000), lng: -115.476157, lat: 33.854822 },
          { at: storyAt(ORIGIN, 8000), lng: -115.479463, lat: 33.856203 },
          { at: storyAt(ORIGIN, 12000), lng: -115.482944, lat: 33.857018 },
          { at: storyAt(ORIGIN, 16000), lng: -115.486537, lat: 33.857764 },
          { at: storyAt(ORIGIN, 20000), lng: -115.482944, lat: 33.857018 },
          { at: storyAt(ORIGIN, 24000), lng: -115.479463, lat: 33.856203 },
          { at: storyAt(ORIGIN, 28000), lng: -115.476157, lat: 33.854822 },
          { at: storyAt(ORIGIN, 32000), lng: -115.47288, lat: 33.853382 },
        ],
        comments: [
          {
            id: 'tr-c1',
            createdAt: storyAt(ORIGIN, 0),
            createdAtWallIso: '2026-09-11T05:00:00.000Z',
            authorObjectId: 'truck-1',
            source: 'script',
            body: 'Truck is cycling Kaiser Road — following the linked asset until the road closes.',
          },
        ],
      },
      {
        id: 'burst-1',
        type: 'pipeburst',
        label: 'West collector burst',
        scope: 'local',
        pose: { lng: -115.4831, lat: 33.8534 },
        size: 'lg',
        startsAt: storyAt(ORIGIN, 20_000),
        description: 'Rupture on the west collector. Isolate downstream valves.',
        params: { radiusM: 55, product: 'product line' },
        comments: [
          {
            id: 'burst-c1',
            createdAt: storyAt(ORIGIN, 20_000),
            createdAtWallIso: '2026-09-11T05:00:20.000Z',
            authorObjectId: 'burst-1',
            source: 'script',
            body: 'Possible cause: fatigue at the west collector flange. Isolate downstream valves.',
          },
        ],
      },
      {
        id: 'ems-1',
        type: 'ems',
        label: 'Ambulance',
        scope: 'local',
        pose: WEST_CMD,
        size: 'lg',
        startsAt: storyAt(ORIGIN, 78_000),
        description: 'Staged at West command, then jumps Yucca Drive and parks beside the school — not in the fire.',
        params: {},
        keyframes: [
          { at: storyAt(ORIGIN, 0), lng: WEST_CMD.lng, lat: WEST_CMD.lat },
          { at: storyAt(ORIGIN, 78_000), lng: WEST_CMD.lng, lat: WEST_CMD.lat },
          { at: storyAt(ORIGIN, 84_000), lng: -115.4872, lat: 33.8568 },
          { at: storyAt(ORIGIN, 88_000), lng: -115.4838, lat: 33.8554 },
          { at: storyAt(ORIGIN, 92_000), lng: -115.4804, lat: 33.8538 },
          { at: storyAt(ORIGIN, 96_000), lng: -115.4772, lat: 33.8528 },
          { at: storyAt(ORIGIN, 100_000), lng: SCHOOL_BAY.lng, lat: SCHOOL_BAY.lat },
        ],
        comments: [
          {
            id: 'ems-c1',
            createdAt: storyAt(ORIGIN, 78_000),
            createdAtWallIso: '2026-09-11T05:01:18.000Z',
            authorObjectId: 'ems-1',
            source: 'script',
            body: 'Ambulance rolling from West command down Yucca toward the school.',
          },
        ],
      },
      {
        id: 'fire-1',
        type: 'fire',
        label: 'School-side fire',
        scope: 'local',
        pose: SCHOOL_FIRE,
        size: 'lg',
        startsAt: storyAt(ORIGIN, 50_000),
        description:
          'Brush fire on the school side of the grid. Keep apparatus clear of Yucca until EMS is on scene.',
        params: { radiusM: 40 },
        radiusKeyframes: [
          { at: storyAt(ORIGIN, 50_000), value: 40 },
          { at: storyAt(ORIGIN, 64_000), value: 95 },
          { at: storyAt(ORIGIN, 70_000), value: 160 },
        ],
        comments: [],
      },
      {
        id: 'leak-1',
        type: 'gas_leak',
        label: 'Tank farm leak',
        scope: 'local',
        pose: TANK_FARM,
        size: 'lg',
        startsAt: storyAt(ORIGIN, 10_000),
        description: 'Vapor leak at the tanks west of West command. Plume follows surface wind.',
        params: { radiusM: 40 },
        comments: [],
      },
      {
        id: 'note-1',
        type: 'label',
        label: 'HOLD NORTH',
        scope: 'local',
        pose: NOTE_POS,
        size: 'xl',
        startsAt: storyAt(ORIGIN, 45_000),
        description: 'Command annotation dropped on the board after the road close.',
        params: { text: 'HOLD NORTH — NO THRU TRAFFIC' },
        comments: [],
      },
    ],
    timeline: [
      {
        id: 'ev-start',
        at: storyAt(ORIGIN, 0),
        title: 'Scenario start',
        summary: 'Multiple failures stack near Eagle Mountain. Watch Kaiser Road, then the west collector and the school.',
        iconType: 'start',
        hiddenFromParticipants: false,
        actions: [],
      },
      {
        id: 'ev-leak',
        at: storyAt(ORIGIN, 10_000),
        title: 'Tank farm leak',
        summary: 'Gas leak at the tanks west of West command.',
        iconType: 'gas_leak',
        hiddenFromParticipants: false,
        actions: [{ kind: 'reveal', objectId: 'leak-1' }],
      },
      {
        id: 'ev-burst',
        at: storyAt(ORIGIN, 20_000),
        title: 'Pipeburst',
        summary: 'West collector ruptures. Plume follows surface wind.',
        iconType: 'pipeburst',
        hiddenFromParticipants: false,
        actions: [{ kind: 'reveal', objectId: 'burst-1' }],
      },
      {
        id: 'ev-road',
        at: storyAt(ORIGIN, 40_000),
        title: 'Kaiser Road closed',
        summary: 'Road closes. The truck is held at the work zone.',
        iconType: 'road',
        hiddenFromParticipants: false,
        actions: [{ kind: 'setParams', objectId: 'road-1', params: { status: 'closed' } }],
      },
      {
        id: 'ev-note',
        at: storyAt(ORIGIN, 45_000),
        title: 'Command note',
        summary: 'HOLD NORTH annotation appears on the north desert.',
        iconType: 'label',
        hiddenFromParticipants: false,
        actions: [{ kind: 'reveal', objectId: 'note-1' }],
      },
      {
        id: 'ev-fire',
        at: storyAt(ORIGIN, 50_000),
        title: 'Fire near school',
        summary: 'Small burn on the school side of the grid.',
        iconType: 'fire',
        hiddenFromParticipants: true,
        actions: [{ kind: 'reveal', objectId: 'fire-1' }],
      },
      {
        id: 'ev-wind',
        at: storyAt(ORIGIN, 58_000),
        title: 'Wind gust',
        summary: 'Wind jumps to 28 mph from the NW and drives the fire.',
        iconType: 'wind',
        hiddenFromParticipants: false,
        actions: [
          {
            kind: 'setParams',
            objectId: 'wind-1',
            params: { fromDeg: 300, speedMph: 28, plumeDistanceM: 900, plumeSpreadDeg: 34 },
          },
        ],
      },
      {
        id: 'ev-fire-grow',
        at: storyAt(ORIGIN, 64_000),
        title: 'Fire spreading',
        summary: 'The burn area jumps outward after the gust.',
        iconType: 'fire',
        hiddenFromParticipants: true,
        actions: [{ kind: 'reveal', objectId: 'fire-1' }],
      },
      {
        id: 'ev-fire-grow-2',
        at: storyAt(ORIGIN, 70_000),
        title: 'Fire growing',
        summary: 'Second jump — the isolation ring gets dramatically larger.',
        iconType: 'fire',
        hiddenFromParticipants: true,
        actions: [{ kind: 'reveal', objectId: 'fire-1' }],
      },
      {
        id: 'ev-ems',
        at: storyAt(ORIGIN, 78_000),
        title: 'Ambulance dispatched',
        summary: 'Leaves West command down Yucca after the fire grows.',
        iconType: 'ems',
        hiddenFromParticipants: false,
        actions: [{ kind: 'reveal', objectId: 'ems-1' }],
      },
      {
        id: 'ev-arrive',
        at: storyAt(ORIGIN, 100_000),
        title: 'Ambulance on scene',
        summary: 'EMS staged at the school parking, clear of the fire.',
        iconType: 'ems',
        hiddenFromParticipants: false,
        actions: [{ kind: 'reveal', objectId: 'ems-1' }],
      },
    ],
  }
}
